<template>
  <section class="d-grid gap-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h4 mb-1">Establecimientos</h2>
        <p class="text-muted mb-0">Cada establecimiento representa una sucursal de la empresa.</p>
      </div>
      <button class="btn btn-outline-dark btn-sm" @click="loadRows" :disabled="loading">Recargar</button>
    </header>

    <div class="card shadow-sm">
      <div class="card-body">
        <form class="row g-3" @submit.prevent="saveRow">
          <div class="col-md-2">
            <label class="form-label">Sucursal</label>
            <input v-model.number="form.sucursal_id" class="form-control" type="number" min="1" />
          </div>
          <div class="col-md-2">
            <label class="form-label">Código</label>
            <input v-model="form.codigo_establecimiento" class="form-control" type="text" maxlength="3" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Nombre comercial de sucursal</label>
            <input v-model="form.nombre_establecimiento" class="form-control" type="text" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Dirección</label>
            <input v-model="form.direccion" class="form-control" type="text" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Teléfono</label>
            <input v-model="form.telefono" class="form-control" type="text" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Horario de atención</label>
            <input v-model="form.horario_atencion" class="form-control" type="text" placeholder="Lunes a sábado, 8:00 a 19:00" />
            <div class="form-text">Se muestra en el ecommerce cuando este local está activo (retiro).</div>
          </div>
          <div class="col-md-4">
            <label class="form-label">Logo comercial en sistema URL</label>
            <input v-model="form.logo_sistema_url" class="form-control" type="text" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Logo factura URL</label>
            <input v-model="form.logo_factura_url" class="form-control" type="text" />
          </div>
          <div class="col-md-4 d-flex align-items-center">
            <div class="form-check form-switch mt-4">
              <input v-model="form.activo" class="form-check-input" type="checkbox" id="establecimiento-activo" />
              <label class="form-check-label" for="establecimiento-activo">
                Activo (visible como local de retiro en el ecommerce)
              </label>
            </div>
          </div>
          <div class="col-12 d-flex gap-2">
            <button class="btn btn-dark" :disabled="saving">{{ saving ? 'Guardando...' : submitLabel }}</button>
            <button class="btn btn-outline-secondary" type="button" @click="resetForm">Limpiar</button>
          </div>
        </form>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>ID</th>
              <th>Sucursal</th>
              <th>Código</th>
              <th>Nombre</th>
              <th>Dirección</th>
              <th>Activo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center text-muted py-4">Cargando...</td>
            </tr>
            <tr v-else-if="!rows.length">
              <td colspan="7" class="text-center text-muted py-4">No hay establecimientos cargados.</td>
            </tr>
            <tr v-for="row in rows" :key="row.establecimiento_id">
              <td>{{ row.establecimiento_id }}</td>
              <td>{{ row.sucursal_id }}</td>
              <td>{{ row.codigo_establecimiento }}</td>
              <td>{{ row.nombre_establecimiento }}</td>
              <td>{{ row.direccion || '—' }}</td>
              <td>
                <span :class="['badge', row.activo ? 'bg-success' : 'bg-secondary']">
                  {{ row.activo ? 'Sí' : 'No' }}
                </span>
              </td>
              <td class="text-end">
                <button class="btn btn-outline-dark btn-sm" @click="editRow(row)">Editar</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import maintenanceFiscalService from '@/services/maintenanceFiscalService'

const loading = ref(false)
const saving = ref(false)
const rows = ref([])
const form = reactive({
  establecimiento_id: null,
  sucursal_id: 1,
  codigo_establecimiento: '001',
  nombre_establecimiento: '',
  direccion: '',
  telefono: '',
  horario_atencion: '',
  logo_sistema_url: '',
  logo_factura_url: '',
  activo: true
})

const submitLabel = computed(() => form.establecimiento_id ? 'Actualizar establecimiento' : 'Crear establecimiento')

function resetForm() {
  form.establecimiento_id = null
  form.sucursal_id = 1
  form.codigo_establecimiento = '001'
  form.nombre_establecimiento = ''
  form.direccion = ''
  form.telefono = ''
  form.horario_atencion = ''
  form.logo_sistema_url = ''
  form.logo_factura_url = ''
  form.activo = true
}

function editRow(row) {
  form.establecimiento_id = row.establecimiento_id
  form.sucursal_id = row.sucursal_id
  form.codigo_establecimiento = row.codigo_establecimiento || ''
  form.nombre_establecimiento = row.nombre_establecimiento || ''
  form.direccion = row.direccion || ''
  form.telefono = row.telefono || ''
  form.horario_atencion = row.horario_atencion || ''
  form.logo_sistema_url = row.logo_sistema_url || ''
  form.logo_factura_url = row.logo_factura_url || ''
  form.activo = row.activo !== false
}

async function loadRows() {
  loading.value = true
  try {
    rows.value = await maintenanceFiscalService.listEstablishments() || []
  } catch (error) {
    console.error('Error cargando establecimientos', error)
    window.alert(error.userMessage || 'No se pudo cargar establecimientos')
  } finally {
    loading.value = false
  }
}

async function saveRow() {
  saving.value = true
  try {
    const payload = {
      sucursal_id: form.sucursal_id,
      codigo_establecimiento: form.codigo_establecimiento,
      nombre_establecimiento: form.nombre_establecimiento,
      direccion: form.direccion,
      telefono: form.telefono,
      horario_atencion: form.horario_atencion,
      logo_sistema_url: form.logo_sistema_url,
      logo_factura_url: form.logo_factura_url,
      activo: form.activo
    }
    if (form.establecimiento_id) {
      await maintenanceFiscalService.updateEstablishment(form.establecimiento_id, payload)
    } else {
      await maintenanceFiscalService.createEstablishment(payload)
    }
    resetForm()
    await loadRows()
  } catch (error) {
    console.error('Error guardando establecimiento', error)
    window.alert(error.userMessage || 'No se pudo guardar el establecimiento')
  } finally {
    saving.value = false
  }
}

onMounted(loadRows)
</script>
